import { stripVTControlCharacters } from 'node:util'

import * as p from '@clack/prompts'

/**
 * clack@1.2.x's spinner erases the previous tick by measuring the bare
 * message, but each tick actually renders `frame + two spaces + message + up
 * to three animated dots`. When that rendered line wraps and the bare message
 * does not, the erase misses the first wrapped row and every tick prints a
 * stale copy of it. Rendered width overhead: 1 frame char + 2 spaces +
 * 3 dots, plus 1 column so the line never sits exactly on the terminal edge.
 */
const RENDER_OVERHEAD = 7

/** Shortest message budget kept even on absurdly narrow terminals. */
const MIN_MESSAGE_WIDTH = 8

/**
 * Truncate a spinner message so the rendered tick line always fits on one
 * terminal row. When stdout is not a TTY the width is unknown; mirror clack's
 * own 80-column assumption.
 */
export function fitSpinnerMessage(message: string): string {
  const columns = process.stdout.columns ?? 80
  const max = Math.max(columns - RENDER_OVERHEAD, MIN_MESSAGE_WIDTH)
  const visible = stripVTControlCharacters(message)
  if (visible.length <= max) return message
  // Truncate the color-stripped text so the slice cannot cut an escape
  // sequence in half; losing styling on this rare path is fine.
  return `${visible.slice(0, max - 1)}…`
}

/**
 * Rows a settled clack log/spinner line occupies on screen: the `│` guide row
 * clack prints above it plus the message row itself.
 */
const CLACK_LINE_ROWS = 2

/** Rendered width of clack's log prefix: symbol + two spaces. */
const LOG_PREFIX_WIDTH = 3

/**
 * Rows a settled clack log line occupies for a given single-line message: the
 * `│` guide row plus however many terminal rows the rendered message wraps to.
 * Use this instead of assuming CLACK_LINE_ROWS when the message may exceed the
 * terminal width. Off-TTY mirrors clack's 80-column assumption.
 */
export function clackLogRows(message: string): number {
  const columns = process.stdout.columns ?? 80
  const width = stripVTControlCharacters(message).length + LOG_PREFIX_WIDTH
  return 1 + Math.max(1, Math.ceil(width / columns))
}

/**
 * Erase the last `rows` terminal rows via ANSI delete-line; the cursor must be
 * at the start of the line just below them, where it ends up again. No-op
 * off-TTY (CI, pipes) where cursor movement would corrupt plain output.
 */
export function eraseRows(rows: number): void {
  if (rows <= 0 || !process.stdout.isTTY || process.env.CI === 'true') return
  process.stdout.write(`\x1b[${rows}A\x1b[${rows}M`)
}

/**
 * Erase a previously printed clack log/spinner line from the terminal via
 * ANSI delete-line, shifting any rows printed after it upward. `linesBelow` is
 * the number of settled clack lines printed since the target line — callers
 * must know exactly what followed it; a wrapped line breaks the row math, so
 * only use this for short messages. No-op off-TTY (CI, pipes) where cursor
 * movement would corrupt plain output.
 */
export function eraseClackLine(options: { linesBelow?: number } = {}): void {
  if (!process.stdout.isTTY || process.env.CI === 'true') return
  const below = (options.linesBelow ?? 0) * CLACK_LINE_ROWS
  const up = below + CLACK_LINE_ROWS
  const restore = below > 0 ? `\x1b[${below}B` : ''
  process.stdout.write(`\x1b[${up}A\x1b[${CLACK_LINE_ROWS}M${restore}`)
}

export interface Spinner {
  start(message?: string): void
  message(message?: string): void
  stop(message?: string): void
  /** Erase the spinner without printing a final line. */
  clear(): void
}

let activeSpinners = 0

/**
 * Whether any wrapped spinner is currently animating. While one is, clack's
 * spinner prints its own `cancelMessage` line from a `process.on('exit')`
 * hook, so a SIGINT handler that exits the process must not print a second
 * cancellation line (see `installSetupCancelGuard`).
 */
export function hasActiveSpinner(): boolean {
  return activeSpinners > 0
}

/**
 * Drop-in replacement for `p.spinner()`. Animated text (`start`, `message`)
 * is width-limited; `stop` text is printed once and left to wrap naturally.
 * `clear()` erases the spinner without a final line — including the leading
 * `│` guide line clack writes at `start()` and leaves behind on its own
 * `clear()` — so a cleared spinner leaves no trace at all.
 */
export function spinner(options?: { withGuide?: boolean }): Spinner {
  // cancelMessage is what clack prints for a spinner interrupted by a signal
  // or process exit — match the prompts' cancellation wording.
  const inner = p.spinner({ ...options, cancelMessage: 'Setup cancelled.' })
  const guided = options?.withGuide !== false
  let started = false
  const setStarted = (next: boolean) => {
    if (started === next) return
    started = next
    activeSpinners += next ? 1 : -1
  }
  return {
    start: (message = '') => {
      setStarted(true)
      inner.start(fitSpinnerMessage(message))
    },
    message: (message = '') => inner.message(fitSpinnerMessage(message)),
    stop: (message = '') => {
      setStarted(false)
      inner.stop(message)
    },
    clear: () => {
      if (!started) return
      setStarted(false)
      inner.clear()
      if (guided && process.stdout.isTTY && process.env.CI !== 'true') {
        process.stdout.write('\x1b[1A\x1b[2K')
      }
    }
  }
}
